import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import { toIsoDate } from "../ApexDatePicker";
import { Loader2, X, User, Wrench, CalendarCheck } from "lucide-react";

const PART_LABEL = { morning: "Dopoledne", afternoon: "Odpoledne" };
const MONTH_NAMES = [
  "Leden", "Únor", "Březen", "Duben", "Květen", "Červen",
  "Červenec", "Srpen", "Září", "Říjen", "Listopad", "Prosinec",
];

// Builds a Monday-start calendar grid (array of weeks, each 7 cells) for a
// given year/month (0-indexed). Cells outside the month are null.
function buildMonthGrid(year, month) {
  const first = new Date(year, month, 1);
  const startWeekday = (first.getDay() + 6) % 7; // Monday = 0
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(toIsoDate(new Date(year, month, d)));
  while (cells.length % 7 !== 0) cells.push(null);
  const weeks = [];
  for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));
  return weeks;
}

function HalfCell({ dateStr, part, data, disabled, busy, onToggle, onDetail }) {
  const status = data?.status;
  let cls = "flex-1 h-5 rounded text-[8px] font-bold flex items-center justify-center transition-all ";
  if (disabled || !data) cls += "bg-transparent text-apex-cream/10 cursor-default";
  else if (status === "booked") cls += "bg-neutral-500 text-apex-cream border-2 border-apex-gold cursor-pointer hover:brightness-110";
  else if (status === "blocked") cls += "bg-neutral-500/85 text-apex-cream/70 cursor-pointer hover:brightness-125";
  else cls += "bg-neutral-800 text-apex-cream/35 cursor-pointer hover:bg-neutral-700";

  const handleClick = () => {
    if (disabled || !data || busy) return;
    if (status === "booked") onDetail(dateStr, part, data.bookings);
    else onToggle(dateStr, part);
  };

  return (
    <button
      type="button"
      disabled={disabled || !data || busy}
      onClick={handleClick}
      className={cls}
      title={data ? `${PART_LABEL[part]}${status === "booked" ? " — obsazeno (klik pro detail)" : status === "blocked" ? " — blokováno (klik odblokuje)" : " — volno (klik zablokuje)"}` : ""}
      data-testid={`cal-half-${dateStr}-${part}`}
    >
      {part === "morning" ? "D" : "O"}
    </button>
  );
}

function MonthGrid({ year, month, dataByDate, busyDate, onToggle, onDetail }) {
  const weeks = buildMonthGrid(year, month);
  return (
    <div className="rounded-2xl bg-apex-card border border-apex-line p-4">
      <div className="text-center font-display text-apex-gold font-semibold mb-3 tracking-wide">
        {MONTH_NAMES[month]} {year}
      </div>
      <div className="grid grid-cols-7 gap-1 mb-1 text-[10px] text-apex-cream/45 text-center uppercase">
        {["Po", "Út", "St", "Čt", "Pá", "So", "Ne"].map((d) => <div key={d}>{d}</div>)}
      </div>
      {weeks.map((week, wi) => (
        <div key={wi} className="grid grid-cols-7 gap-1 mb-1">
          {week.map((dateStr, di) => {
            if (!dateStr) return <div key={di} />;
            const day = dataByDate[dateStr];
            const dayNum = Number(dateStr.slice(-2));
            return (
              <div key={dateStr} className="rounded-md border border-apex-line/30 p-0.5" data-testid={`cal-day-${dateStr}`}>
                <div className="text-[9px] text-apex-cream/45 text-center leading-none mb-0.5">{dayNum}</div>
                <div className="flex gap-0.5">
                  <HalfCell dateStr={dateStr} part="morning" data={day?.morning} disabled={!day} busy={busyDate === dateStr} onToggle={onToggle} onDetail={onDetail} />
                  <HalfCell dateStr={dateStr} part="afternoon" data={day?.afternoon} disabled={!day} busy={busyDate === dateStr} onToggle={onToggle} onDetail={onDetail} />
                </div>
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}

/**
 * Admin/employee 2-month half-day overview — replaces the old exact-time
 * blocking UI. Purely a personal planning dashboard: gray = manually
 * blocked, dark gray = free, gold-bordered = a negotiated ("Sjednaný
 * termín") booking is anchored there. Click a free/blocked half to
 * toggle it; click a booked half to see which booking(s) it belongs to.
 */
export default function AdminCalendarOverview() {
  const [dataByDate, setDataByDate] = useState({});
  const [loading, setLoading] = useState(true);
  const [busyDate, setBusyDate] = useState(null);
  const [detail, setDetail] = useState(null); // { dateStr, part, bookings }

  const load = () => {
    setLoading(true);
    api.get("/admin/calendar-overview", { params: { months: 2 } })
      .then((r) => {
        const map = {};
        (r.data?.days || []).forEach((d) => { map[d.date] = d; });
        setDataByDate(map);
      })
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const toggleHalf = async (dateStr, part) => {
    const day = dataByDate[dateStr];
    if (!day) return;
    const otherPart = part === "morning" ? "afternoon" : "morning";
    const otherBlocked = day[otherPart]?.status === "blocked";
    const targetBlocked = day[part]?.status === "blocked";
    const newParts = [];
    if (otherBlocked) newParts.push(otherPart);
    if (!targetBlocked) newParts.push(part);
    setBusyDate(dateStr);
    try {
      await api.put(`/admin/blocked-half-days/${dateStr}`, { parts: newParts });
      await load();
    } catch (e) {
      alert(e?.response?.data?.detail || "Změnu se nepodařilo uložit.");
    } finally {
      setBusyDate(null);
    }
  };

  const now = new Date();
  const nextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1);

  return (
    <div>
      <h3 className="font-display text-2xl text-apex-cream font-bold mb-1">Kalendář – 2měsíční přehled</h3>
      <p className="text-sm text-apex-cream/60 mb-4">
        Osobní přehled dostupnosti. Kliknutím na volný/blokovaný půlden ho zablokujete/odblokujete. Kliknutím na obsazený půlden zobrazíte detail rezervace.
      </p>

      <div className="flex flex-wrap items-center gap-4 text-xs text-apex-cream/70 mb-5" data-testid="cal-legend">
        <span className="inline-flex items-center gap-1.5"><span className="w-3.5 h-3.5 rounded bg-neutral-500/85" /> Blokováno</span>
        <span className="inline-flex items-center gap-1.5"><span className="w-3.5 h-3.5 rounded bg-neutral-800" /> Volno</span>
        <span className="inline-flex items-center gap-1.5"><span className="w-3.5 h-3.5 rounded bg-neutral-500 border-2 border-apex-gold" /> Obsazeno</span>
        {loading && <Loader2 className="w-4 h-4 animate-spin text-apex-gold" />}
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <MonthGrid year={now.getFullYear()} month={now.getMonth()} dataByDate={dataByDate} busyDate={busyDate} onToggle={toggleHalf} onDetail={(d, p, b) => setDetail({ dateStr: d, part: p, bookings: b })} />
        <MonthGrid year={nextMonth.getFullYear()} month={nextMonth.getMonth()} dataByDate={dataByDate} busyDate={busyDate} onToggle={toggleHalf} onDetail={(d, p, b) => setDetail({ dateStr: d, part: p, bookings: b })} />
      </div>

      {detail && (
        <div className="mt-5 rounded-2xl bg-apex-card border border-apex-gold/40 p-5" data-testid="cal-detail-panel">
          <div className="flex items-center justify-between mb-3">
            <div className="text-apex-gold font-semibold flex items-center gap-2">
              <CalendarCheck className="w-4 h-4" />
              {new Date(`${detail.dateStr}T00:00:00`).toLocaleDateString("cs-CZ", { weekday: "long", day: "numeric", month: "long" })} · {PART_LABEL[detail.part]}
            </div>
            <button onClick={() => setDetail(null)} className="text-apex-cream/50 hover:text-apex-gold" data-testid="cal-detail-close">
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="space-y-2">
            {detail.bookings.map((b) => (
              <div key={b.id} className="rounded-xl bg-apex-bg border border-apex-line p-3 text-sm text-apex-cream/85" data-testid={`cal-detail-booking-${b.id}`}>
                <div className="flex items-center gap-2 font-semibold text-apex-cream"><User className="w-3.5 h-3.5 text-apex-gold" /> {b.customerName || "—"}</div>
                <div className="flex items-center gap-2 mt-1 text-apex-cream/70"><Wrench className="w-3.5 h-3.5 text-apex-gold" /> {b.service} · {b.time}</div>
                {b.termAgreedText && <div className="mt-1 text-apex-cream/60 italic">„{b.termAgreedText}“</div>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
